/**
 * Nmbrs Team Calendar
 *
 * Monthly grid calendar showing all linked employees and their leave periods.
 * Story 3.4: Team Verlofkalender
 *
 * Layout:
 * - Month navigation header
 * - Grid: employee rows × days of month
 * - Colored leave blocks clipped to visible month
 * - Weekend columns: gray background
 * - Holiday columns: yellow highlight
 * - Click on leave block: popover with details
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { useTeamLeave, useHolidays, type TeamLeaveItem } from '../hooks/useNmbrsLeave';
import { NmbrsCalendarLegend, LEAVE_TYPE_COLORS, DEFAULT_LEAVE_COLOR } from './NmbrsCalendarLegend';

// ============================================
// Utility helpers
// ============================================

/** Pad a number to 2 digits. */
function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Return ISO date string for the first day of the given month/year. */
function firstDayOfMonth(year: number, month: number): string {
  return `${year}-${pad2(month)}-01`;
}

/** Return ISO date string for the last day of the given month/year. */
function lastDayOfMonth(year: number, month: number): string {
  const last = new Date(year, month, 0).getDate(); // day 0 of next month = last of this month
  return `${year}-${pad2(month)}-${pad2(last)}`;
}

/** Return the number of days in a given month/year. */
function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/**
 * Return day-of-week (0=Sunday … 6=Saturday) for a given ISO date string.
 * Parses the date in LOCAL time to avoid UTC midnight shifting the weekday
 * in timezones east of UTC (e.g. CET/CEST in the Netherlands).
 */
function dayOfWeek(dateStr: string): number {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year!, month! - 1, day!).getDay();
}

/** Clamp a date string to [min, max]. */
function clampDate(date: string, min: string, max: string): string {
  if (date < min) return min;
  if (date > max) return max;
  return date;
}

const MONTH_NAMES_NL = [
  'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
  'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December',
];

// ============================================
// Leave Block Popover
// ============================================

interface LeavePopoverProps {
  leave: TeamLeaveItem;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
}

function LeavePopover({ leave, onClose, anchorRef }: LeavePopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, anchorRef]);

  const formatDate = (d: string) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const statusLabel: Record<string, string> = {
    Approved: 'Goedgekeurd',
    Pending: 'In behandeling',
    Rejected: 'Afgewezen',
  };

  return (
    <div
      ref={popoverRef}
      className="absolute z-50 bg-white rounded-lg border border-[#E2E8F0] shadow-lg p-3 min-w-[200px] text-xs text-[#1E293B]"
      style={{ top: '100%', left: 0, marginTop: 4 }}
    >
      <div className="font-semibold text-sm mb-2">{leave.type}</div>
      <div className="space-y-1">
        <div>
          <span className="text-gray-500">Van: </span>
          {formatDate(leave.startDate)}
        </div>
        <div>
          <span className="text-gray-500">Tot: </span>
          {formatDate(leave.endDate)}
        </div>
        <div>
          <span className="text-gray-500">Uren: </span>
          {leave.hours}
        </div>
        <div>
          <span className="text-gray-500">Status: </span>
          {statusLabel[leave.status] ?? leave.status}
        </div>
        {leave.description && (
          <div>
            <span className="text-gray-500">Omschrijving: </span>
            {leave.description}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Leave Block Cell
// ============================================

interface LeaveBlockProps {
  leave: TeamLeaveItem;
}

function LeaveBlock({ leave }: LeaveBlockProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  const color = LEAVE_TYPE_COLORS[leave.leaveType] ?? DEFAULT_LEAVE_COLOR;

  return (
    <div className="relative">
      <button
        ref={ref}
        onClick={() => setOpen((v) => !v)}
        className="w-full h-5 rounded-sm cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#2F5A7A] opacity-90 hover:opacity-100 transition-opacity"
        style={{ backgroundColor: color }}
        title={`${leave.type} (${leave.hours}u)`}
        aria-label={`Verlof: ${leave.type}`}
      />
      {open && (
        <LeavePopover
          leave={leave}
          onClose={() => setOpen(false)}
          anchorRef={ref as React.RefObject<HTMLElement | null>}
        />
      )}
    </div>
  );
}

// ============================================
// Main Calendar Component
// ============================================

export function NmbrsTeamCalendar() {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1); // 1-based

  const startDate = firstDayOfMonth(currentYear, currentMonth);
  const endDate = lastDayOfMonth(currentYear, currentMonth);
  const numDays = daysInMonth(currentYear, currentMonth);

  const { data: teamLeaveData, isLoading, isError } = useTeamLeave(startDate, endDate);
  const { data: holidaysData } = useHolidays(currentYear);

  // Build a Set of holiday date strings for O(1) lookup (memoized)
  const holidayDates = useMemo(
    () => new Set<string>(holidaysData?.data?.map((h) => h.date.substring(0, 10)) ?? []),
    [holidaysData]
  );

  // Day columns: array of ISO date strings for each day in the month (memoized)
  const dayColumns = useMemo(
    () =>
      Array.from({ length: numDays }, (_, i) =>
        `${currentYear}-${pad2(currentMonth)}-${pad2(i + 1)}`
      ),
    [currentYear, currentMonth, numDays]
  );

  // Navigate to previous month
  const prevMonth = () => {
    if (currentMonth === 1) {
      setCurrentYear((y) => y - 1);
      setCurrentMonth(12);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  // Navigate to next month
  const nextMonth = () => {
    if (currentMonth === 12) {
      setCurrentYear((y) => y + 1);
      setCurrentMonth(1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  // Collect all leave types present in this view for legend (memoized)
  const presentTypes = useMemo(() => {
    const types = new Set<number>();
    teamLeaveData?.data?.forEach((emp) =>
      emp.leaves.forEach((l) => types.add(l.leaveType))
    );
    return types;
  }, [teamLeaveData]);

  /**
   * Pre-compute a per-employee day-to-leave lookup to avoid O(employees * days * leaves)
   * inline filtering during render.
   *
   * Structure: Map<employeeId, Map<dayStr, TeamLeaveItem[]>>
   */
  const leavesByEmployeeAndDay = useMemo(() => {
    const result = new Map<number, Map<string, TeamLeaveItem[]>>();
    if (!teamLeaveData?.data) return result;

    for (const emp of teamLeaveData.data) {
      const dayMap = new Map<string, TeamLeaveItem[]>();
      for (const leave of emp.leaves) {
        const clampedStart = clampDate(leave.startDate, startDate, endDate);
        const clampedEnd = clampDate(leave.endDate, startDate, endDate);
        for (const dayStr of dayColumns) {
          if (dayStr >= clampedStart && dayStr <= clampedEnd) {
            const existing = dayMap.get(dayStr) ?? [];
            existing.push(leave);
            dayMap.set(dayStr, existing);
          }
        }
      }
      result.set(emp.employeeId, dayMap);
    }
    return result;
  }, [teamLeaveData, startDate, endDate, dayColumns]);

  /** Get leave blocks for a given employee on a specific day (O(1) lookup). */
  function getLeavesForDay(employeeId: number, dayStr: string): TeamLeaveItem[] {
    return leavesByEmployeeAndDay.get(employeeId)?.get(dayStr) ?? [];
  }

  return (
    <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm overflow-hidden">
      {/* Header: Month navigation */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0] bg-[#2F5A7A]">
        <button
          onClick={prevMonth}
          className="p-1.5 rounded hover:bg-[#4A7A9B] text-white transition-colors"
          aria-label="Vorige maand"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <h2 className="text-sm font-semibold text-white">
          {MONTH_NAMES_NL[currentMonth - 1]} {currentYear}
        </h2>

        <button
          onClick={nextMonth}
          className="p-1.5 rounded hover:bg-[#4A7A9B] text-white transition-colors"
          aria-label="Volgende maand"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2F5A7A]" />
          <span className="ml-3 text-sm text-gray-500">Verlofkalender laden...</span>
        </div>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <div className="p-8 text-center">
          <p className="text-sm text-[#D64545]">Kon verlofgegevens niet laden.</p>
          <p className="text-xs text-gray-400 mt-1">Controleer de Nmbrs verbinding en probeer opnieuw.</p>
        </div>
      )}

      {/* Empty state: no linked employees */}
      {!isLoading && !isError && teamLeaveData?.data?.length === 0 && (
        <div className="p-8 text-center">
          <p className="text-sm text-gray-500">Geen gekoppelde medewerkers gevonden.</p>
          <p className="text-xs text-gray-400 mt-1">
            Koppel medewerkers via het Koppelingen tabblad om de verlofkalender te gebruiken.
          </p>
        </div>
      )}

      {/* Calendar grid */}
      {!isLoading && !isError && teamLeaveData && teamLeaveData.data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs" style={{ minWidth: `${180 + numDays * 28}px` }}>
            <thead>
              <tr className="bg-gray-50 border-b border-[#E2E8F0]">
                {/* Employee name column header */}
                <th
                  className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-r border-[#E2E8F0]"
                  style={{ minWidth: 160, width: 160 }}
                >
                  Medewerker
                </th>

                {/* Day columns */}
                {dayColumns.map((dayStr) => {
                  const dow = dayOfWeek(dayStr);
                  const isWeekend = dow === 0 || dow === 6;
                  const isHoliday = holidayDates.has(dayStr);
                  const dayNum = parseInt(dayStr.substring(8), 10);

                  return (
                    <th
                      key={dayStr}
                      className={`px-0.5 py-2 text-center font-medium text-gray-500 border-r border-[#E2E8F0] ${
                        isHoliday
                          ? 'bg-[#EFF6FF] border-t-2 border-t-[#54949E]'
                          : isWeekend
                          ? 'bg-[#F3F4F6]'
                          : ''
                      }`}
                      style={{ width: 28, minWidth: 28 }}
                      title={isHoliday ? (holidaysData?.data?.find((h) => h.date.substring(0, 10) === dayStr)?.name ?? 'Feestdag') : undefined}
                    >
                      {dayNum}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {teamLeaveData.data.map((employee, rowIdx) => (
                <tr
                  key={employee.employeeId}
                  className={`border-b border-[#E2E8F0] ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                >
                  {/* Employee name cell */}
                  <td
                    className="sticky left-0 z-10 px-3 py-1.5 font-medium text-[#1E293B] border-r border-[#E2E8F0] truncate"
                    style={{
                      minWidth: 160,
                      width: 160,
                      maxWidth: 160,
                      backgroundColor: rowIdx % 2 === 0 ? '#fff' : '#f9fafb',
                    }}
                    title={employee.employeeName}
                  >
                    {employee.employeeName}
                  </td>

                  {/* Day cells */}
                  {dayColumns.map((dayStr) => {
                    const dow = dayOfWeek(dayStr);
                    const isWeekend = dow === 0 || dow === 6;
                    const isHoliday = holidayDates.has(dayStr);
                    const leavesOnDay = getLeavesForDay(employee.employeeId, dayStr);

                    let cellBg = '';
                    if (isHoliday) cellBg = 'bg-[#EFF6FF] border-t-2 border-t-[#54949E]';
                    else if (isWeekend) cellBg = 'bg-[#F3F4F6]';

                    return (
                      <td
                        key={dayStr}
                        className={`px-0.5 py-1 border-r border-[#E2E8F0] align-middle ${cellBg}`}
                        style={{ width: 28, minWidth: 28 }}
                      >
                        <div className="flex flex-col gap-0.5">
                          {leavesOnDay.map((leave) => (
                            <LeaveBlock key={`${leave.id}-${leave.startDate}`} leave={leave} />
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      {!isLoading && !isError && teamLeaveData && teamLeaveData.data.length > 0 && (
        <NmbrsCalendarLegend presentTypes={Array.from(presentTypes).sort()} />
      )}
    </div>
  );
}
